import React from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, FileSignature, Receipt, TrendingUp, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

function StatCard({ label, value, hint, icon: Icon, tone = "default", testid }) {
  const toneCls =
    tone === "warning"
      ? "bg-warning/10 text-warning"
      : tone === "success"
      ? "bg-success/10 text-success"
      : tone === "destructive"
      ? "bg-destructive/10 text-destructive"
      : "bg-primary/10 text-primary";
  return (
    <Card className="animate-fade-up" data-testid={testid}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {label}
            </div>
            <div className="mt-2 font-display text-3xl sm:text-4xl font-medium tracking-tighter">
              {value}
            </div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
          </div>
          <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${toneCls}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { data, isLoading, error } = useSWR("/dashboard/stats", fetcher);
  const { data: bills } = useSWR("/bills?status=unpaid", fetcher);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col gap-1">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Ringkasan Operasional
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Dashboard
        </h1>
        <p className="text-sm text-muted-foreground">
          Pantau okupansi, pemasukan, dan tagihan terbuka dalam satu tampilan.
        </p>
      </header>

      {error && (
        <div className="text-sm text-destructive">Gagal memuat data.</div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Pemasukan Bulan Ini"
          value={formatIDR(data?.income?.this_month)}
          hint={`Total sepanjang waktu: ${formatIDR(data?.income?.total)}`}
          icon={TrendingUp}
          tone="success"
          testid="stat-income-month"
        />
        <StatCard
          label="Tagihan Terbuka"
          value={formatIDR(data?.income?.outstanding)}
          hint={`${data?.bills?.unpaid_or_partial || 0} tagihan aktif`}
          icon={Receipt}
          tone="warning"
          testid="stat-outstanding"
        />
        <StatCard
          label="Unit Kontrakan"
          value={data?.units?.total ?? 0}
          hint={`Terisi ${data?.units?.terisi || 0} · Kosong ${data?.units?.kosong || 0}`}
          icon={Building2}
          testid="stat-units"
        />
        <StatCard
          label="Kontrak Aktif"
          value={data?.contracts?.active ?? 0}
          hint={`${data?.tenants?.total || 0} penyewa terdaftar`}
          icon={FileSignature}
          testid="stat-contracts"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-display text-xl">Pemasukan 6 Bulan Terakhir</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 -ml-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.chart_income_6m || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                    tickFormatter={(v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v)} />
                  <Tooltip
                    formatter={(v) => formatIDR(v)}
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  />
                  <Bar dataKey="income" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="font-display text-xl">Perlu Perhatian</CardTitle>
            {data?.bills?.overdue > 0 && (
              <Badge variant="destructive" className="rounded-full" data-testid="badge-overdue">
                <AlertTriangle className="h-3 w-3 mr-1" /> {data.bills.overdue} lewat jatuh tempo
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Row label="Kontrak Aktif" value={data?.contracts?.active ?? 0} icon={FileSignature} />
              <Row label="Total Penyewa" value={data?.tenants?.total ?? 0} icon={Users} />
              <Row label="Unit Maintenance" value={data?.units?.maintenance ?? 0} icon={Building2} />
              <Row label="Tagihan Overdue" value={data?.bills?.overdue ?? 0} icon={AlertTriangle} tone="destructive" />
            </div>
            <Link
              to="/bills?status=unpaid"
              data-testid="cta-view-open-bills"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              Lihat semua tagihan terbuka →
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* Recent open bills preview */}
      {bills && bills.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-xl sm:text-2xl font-medium tracking-tight">Tagihan Terbaru</h2>
            <Link to="/bills" className="text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground">
              Lihat semua
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bills.slice(0, 4).map((b) => (
              <Card key={b.id} data-testid={`dashboard-bill-${b.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Jatuh tempo {formatDate(b.due_date)}</div>
                    <div className="font-medium mt-0.5">{formatIDR(b.amount)}</div>
                    <div className="text-xs text-muted-foreground">
                      Periode {formatDate(b.period_start)} – {formatDate(b.period_end)}
                    </div>
                  </div>
                  {b.late_fee > 0 && (
                    <Badge variant="destructive" className="rounded-full">
                      Denda {formatIDR(b.late_fee)}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Expiring contracts alert */}
      {data?.expiring_contracts && data.expiring_contracts.length > 0 && (
        <section data-testid="dashboard-expiring-section">
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <h2 className="font-display text-xl sm:text-2xl font-medium tracking-tight flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-warning" />
              Kontrak Segera Berakhir
            </h2>
            <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Dalam 30 hari</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.expiring_contracts.map((c) => {
              const urgent = c.days_left <= 7;
              return (
                <Card key={c.contract_id} className={urgent ? "border-destructive/50" : "border-warning/50"} data-testid={`dashboard-expiring-${c.contract_id}`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className="rounded-md font-mono">{c.unit_code}</Badge>
                          <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${urgent ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning"}`}>
                            {c.days_left === 0 ? "Hari ini" : `${c.days_left} hari lagi`}
                          </Badge>
                        </div>
                        <div className="mt-2 font-medium truncate">{c.tenant_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          Berakhir {formatDate(c.end_date)} · {formatIDR(c.monthly_rent)}/bln
                        </div>
                      </div>
                      <Link to={`/contracts`}>
                        <Button variant="outline" size="sm" data-testid={`dashboard-review-contract-${c.contract_id}`}>Tinjau</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ label, value, icon: Icon, tone = "default" }) {
  const c =
    tone === "destructive" ? "text-destructive" :
    tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className={`font-medium tabular-nums ${c}`}>{value}</div>
    </div>
  );
}
